// src/pages/admin/ManageSchoolsPage.jsx
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import ProtectedRoute from '../../../components/auth/ProtectedRoute';
import { API_BASE_URL_ELECTIONS } from '../../../utils/constants';
import ConfirmDialog from '../../../components/common/ConfirmDialog';
import ElectionProtectedRoute from '../../../components/auth/ElectionProtectedRoute';

const ManageSchoolsPage = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [schools, setSchools] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingSchool, setEditingSchool] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    shortName: '',
    verificationType: 'INDEX_NUMBER'
  });

  useEffect(() => {
    checkAuth();
    fetchSchools();
  }, []);

  const checkAuth = () => {
    const userData = localStorage.getItem('election_user');
    if (!userData) {
      navigate('/admin/election-login');
      return;
    }
    const user = JSON.parse(userData);
    if (user.role !== 'SUPER_ADMIN') {
      navigate('/admin/election-dashboard');
      return;
    }
    setUser(user);
  };

  const fetchSchools = async () => {
    try {
      const response = await fetch(`${API_BASE_URL_ELECTIONS}/schools`);
      const data = await response.json();
      if (data.success) {
        setSchools(data.data);
      }
    } catch (error) {
      console.error('Error fetching schools:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const token = localStorage.getItem('election_token');
      const url = editingSchool 
        ? `${API_BASE_URL_ELECTIONS}/schools/${editingSchool.id}`
        : `${API_BASE_URL_ELECTIONS}/schools`;
      
      const method = editingSchool ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(formData)
      });

      const data = await response.json();

      if (data.success) {
        setShowModal(false);
        setEditingSchool(null);
        setFormData({ name: '', shortName: '', verificationType: 'INDEX_NUMBER' });
        fetchSchools();
      } else {
        alert(data.message);
      }
    } catch (error) {
      console.error('Error saving school:', error);
      alert('Failed to save school');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (school) => {
    setEditingSchool(school);
    setFormData({
      name: school.name,
      shortName: school.shortName || '',
      verificationType: school.verificationType
    });
    setShowModal(true);
  };

  const handleDelete = async (schoolId) => {
    if (!ConfirmDialog('Are you sure you want to delete this school?')) return;

    try {
      const token = localStorage.getItem('election_token');
      const response = await fetch(`${API_BASE_URL_ELECTIONS}/schools/${schoolId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      const data = await response.json();
      if (data.success) {
        fetchSchools();
      }
    } catch (error) {
      console.error('Error deleting school:', error);
      alert('Failed to delete school');
    }
  };

  if (loading && schools.length === 0) {
    return (
      <ElectionProtectedRoute>
        <div className="flex items-center justify-center min-h-screen bg-black">
          <div className="text-center">
            <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-red-600 mx-auto"></div>
            <p className="mt-4 text-gray-400 font-semibold">Loading Schools...</p>
          </div>
        </div>
      </ElectionProtectedRoute>
    );
  }

  return (
    <ElectionProtectedRoute>
      <div className="min-h-screen bg-black">
        {/* Header */}
        <div className="bg-gradient-to-r from-gray-900 via-black to-gray-900 border-b border-red-900/30 shadow-xl">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
            <div className="flex items-center justify-between">
              <div>
                <button
                  onClick={() => navigate('/admin/election-dashboard')}
                  className="flex items-center gap-2 text-red-400 hover:text-red-300 mb-3 transition-colors"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                  </svg>
                  Back to Dashboard
                </button>
                <h1 className="text-3xl font-bold text-white flex items-center gap-3">
                  <span className="text-red-500">🏫</span>
                  Manage Schools
                </h1>
                <p className="text-gray-400 mt-1">Add and manage educational institutions</p>
              </div>
              <button
                onClick={() => {
                  setEditingSchool(null);
                  setFormData({ name: '', shortName: '', verificationType: 'INDEX_NUMBER' });
                  setShowModal(true);
                }}
                className="px-6 py-3 bg-gradient-to-r from-red-600 to-red-700 hover:from-red-700 hover:to-red-800 text-white rounded-lg font-semibold shadow-lg shadow-red-600/30 transition-all duration-300 transform hover:scale-105 flex items-center gap-2"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
                Add School
              </button>
            </div>
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {/* Schools Grid */}
          {schools.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {schools.map((school) => (
                <div key={school.id} className="group relative">
                  <div className="absolute -inset-0.5 bg-gradient-to-r from-red-600 to-red-700 rounded-xl opacity-0 group-hover:opacity-20 blur transition duration-300"></div>
                  <div className="relative bg-gradient-to-br from-gray-900 to-black border border-gray-800 group-hover:border-red-500/50 rounded-xl shadow-xl p-6 transition-all duration-300">
                    <div className="flex items-start justify-between mb-4">
                      <div className="flex items-start gap-3 flex-1 min-w-0">
                        {school.logo ? (
                          <img src={school.logo} alt={school.name} className="w-14 h-14 rounded-lg object-cover border-2 border-red-500/30 flex-shrink-0" />
                        ) : (
                          // <div className="w-14 h-14 bg-gradient-to-br from-red-600 to-red-700 rounded-lg flex items-center justify-center text-white font-bold text-xl border-2 border-red-500/30 flex-shrink-0">
                          //   {school.shortName || school.name.charAt(0)}
                          // </div>
                           <div className="w-14 h-14 bg-gradient-to-br from-red-600 to-red-700 rounded-lg flex items-center justify-center text-white font-bold text-xl border-2 border-red-500/30">
                            <span className="text-center text-ellipsis overflow-hidden whitespace-nowrap max-w-full text-[clamp(0.8rem,2vw,1.25rem)]">
                              {school.shortName || school.name.charAt(0)}
                            </span>
                          </div>
                        )}
                        <div className="flex-1 min-w-0">
                          <h3 className="font-bold text-white text-lg truncate">{school.name}</h3>
                          {school.shortName && (
                            <p className="text-sm text-gray-400 mt-1">{school.shortName}</p>
                          )}
                        </div>
                      </div>
                      <span className={`px-3 py-1 rounded-full text-xs font-semibold border flex-shrink-0 ml-2 ${
                        school.isActive 
                          ? 'bg-green-900/30 text-green-400 border-green-500/50' 
                          : 'bg-gray-800 text-gray-400 border-gray-600'
                      }`}>
                        {school.isActive ? 'Active' : 'Inactive'}
                      </span>
                    </div>

                    <div className="mb-4 bg-gray-800/50 rounded-lg p-3 border border-gray-700">
                      <p className="text-xs text-gray-400 mb-1">Verification Type</p>
                      <p className="text-sm font-semibold text-white">
                        {school.verificationType.replace('_', ' ')}
                      </p>
                    </div>

                    <div className="flex gap-2">
                      <button
                        onClick={() => handleEdit(school)}
                        className="flex-1 px-4 py-2 bg-gradient-to-r from-gray-700 to-gray-800 hover:from-gray-600 hover:to-gray-700 text-white rounded-lg text-sm font-semibold transition-all duration-300 border border-gray-600 hover:border-red-500/50 flex items-center justify-center gap-2"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                        </svg>
                        Edit
                      </button>
                      <button
                        onClick={() => handleDelete(school.id)}
                        className="flex-1 px-4 py-2 bg-gradient-to-r from-red-600 to-red-700 hover:from-red-700 hover:to-red-800 text-white rounded-lg text-sm font-semibold transition-all duration-300 shadow-lg shadow-red-600/20 hover:shadow-red-600/40 flex items-center justify-center gap-2"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                        </svg>
                        Delete
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="bg-gradient-to-br from-gray-900 to-black border border-red-900/30 rounded-xl shadow-xl p-16">
              <div className="text-center">
                <div className="inline-flex items-center justify-center w-24 h-24 bg-red-900/20 rounded-full mb-6 border border-red-500/30">
                  <svg className="w-12 h-12 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                  </svg>
                </div>
                <h3 className="text-2xl font-bold text-white mb-2">No schools yet</h3>
                <p className="text-gray-400 mb-6 max-w-md mx-auto">Get started by creating your first school to begin managing elections</p>
                <button
                  onClick={() => {
                    setEditingSchool(null);
                    setFormData({ name: '', shortName: '', verificationType: 'INDEX_NUMBER' });
                    setShowModal(true);
                  }}
                  className="px-6 py-3 bg-gradient-to-r from-red-600 to-red-700 hover:from-red-700 hover:to-red-800 text-white rounded-lg font-semibold shadow-lg shadow-red-600/30 transition-all duration-300 transform hover:scale-105 inline-flex items-center gap-2"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                  </svg>
                  Create First School
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4 backdrop-blur-sm">
          <div className="bg-gradient-to-br from-gray-900 to-black border border-red-900/30 rounded-2xl max-w-md w-full p-8 shadow-2xl relative">
            {/* Decorative border glow */}
            <div className="absolute -inset-0.5 bg-gradient-to-r from-red-600 to-red-700 rounded-2xl opacity-20 blur"></div>
            
            <div className="relative">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-2xl font-bold text-white flex items-center gap-3">
                  <span className="text-red-500">
                    {editingSchool ? '✏️' : '➕'}
                  </span>
                  {editingSchool ? 'Edit School' : 'Add New School'}
                </h2>
                <button
                  onClick={() => {
                    setShowModal(false);
                    setEditingSchool(null);
                  }}
                  className="text-gray-400 hover:text-white transition-colors"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              <form onSubmit={handleSubmit} className="space-y-5">
                <div>
                  <label className="block text-sm font-semibold text-gray-300 mb-2">
                    School Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent transition-all"
                    placeholder="University of Ghana"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-300 mb-2">
                    Short Name
                  </label>
                  <input
                    type="text"
                    value={formData.shortName}
                    onChange={(e) => setFormData({ ...formData, shortName: e.target.value })}
                    className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent transition-all"
                    placeholder="UG"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-300 mb-2">
                    Verification Type <span className="text-red-500">*</span>
                  </label>
                  <select
                    value={formData.verificationType}
                    onChange={(e) => setFormData({ ...formData, verificationType: e.target.value })}
                    className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent transition-all"
                    required
                  >
                    <option value="PHONE_NUMBER">Phone Number</option>
                    <option value="EMAIL">Email</option>
                    <option value="INDEX_NUMBER">Index Number</option>
                    <option value="UNIQUE_CODE">Unique Code</option>
                  </select>
                </div>

                <div className="flex gap-3 pt-4">
                  <button
                    type="button"
                    onClick={() => {
                      setShowModal(false);
                      setEditingSchool(null);
                    }}
                    className="flex-1 px-4 py-3 bg-gray-800 border border-gray-700 hover:bg-gray-700 hover:border-gray-600 text-white rounded-lg font-semibold transition-all duration-300"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={loading}
                    className="flex-1 px-4 py-3 bg-gradient-to-r from-red-600 to-red-700 hover:from-red-700 hover:to-red-800 text-white rounded-lg font-semibold shadow-lg shadow-red-600/30 transition-all duration-300 transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
                  >
                    {loading ? (
                      <span className="flex items-center justify-center gap-2">
                        <svg className="animate-spin h-5 w-5" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        Saving...
                      </span>
                    ) : (
                      editingSchool ? 'Update School' : 'Create School'
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </ElectionProtectedRoute>
  );
};

export default ManageSchoolsPage;